/*
  SummaryScreen.jsx
  -----------------
  High-level booking summary page for the Vision booking flow.
  This file composes several smaller, focused components (each extracted
  to `src/components/SummaryScreenSections/`) and lays them out in the
  mobile-width summary container.

  Each extracted section handles its own local UI and behavior (collapse,
  measurements, etc.) so this screen remains a thin composition layer.
*/

import { useRef, useState } from "react";
import EstimatedTotalSection from "../../../components/SummaryScreenSections/EstimatedTotalSection";
import AdditionalNotesSection from "../../../components/SummaryScreenSections/AdditionalNotesSection";
import WhatYoureMovingSection from "../../../components/SummaryScreenSections/WhatYoureMovingSection";
import ArrivalWindowSection from "../../../components/SummaryScreenSections/ArrivalWindowSection";
import TruckAndCrewSection from "../../../components/SummaryScreenSections/TruckAndCrewSection";
import PaymentMethodSection from "../../../components/SummaryScreenSections/PaymentMethodSection";
import PickupDropoffSection from "../../../components/SummaryScreenSections/PickupDropoffSection";



const SummaryScreen = ({
  exactTime,
  deliveryDate,
  dropOffPersonName,
  dropOffPersonPhone,
  pickUpPersonName,
  pickUpPersonPhone,
  pickupLocation,
  stops,
  dropoffLocation,
  items,
  // handleUpdateBookingObject,
  pickUpInstructions,
  dropOffInstructions,
  setBookingTotalModel,
  handleEstimatePrice,





  /// Payment Screen Props

  type,
  bookingTotalModel,
  distanceInKm,
  statesToBookingObj,
  userInfo,
  coupon,
  setShowPaymentMessage,
  handleUpdateBookingObject,
  visionId,
  refreshBookingTotalModel,
  completeBookingObj,



}) => {
  // This component composes the extracted summary sections and supplies
  // the page-level layout only. Detailed behavior lives inside each child
  // component (collapse toggles, resize observers, timeline math, etc.).




  const paymentRef = useRef(null);
  const [requestLoading, setRequestLoading] = useState(false);

  return (
    <div className="px-[33px] font-instrument pb-[32px] max-w-container">
      {/* Page title */}
      <p className="text-[#211F59] text-[24px] font-semibold mb-[21px]" style={{ lineHeight: "23px" }}>
        Booking summary
      </p>

      {/* Short description / instruction */}
      <p className="text-[#211F59] text-[16px] font-semibold mb-[28px]">
        Please review all details carefully to ensure they're correct before
        scheduling.
      </p>

      {/* Estimated total header + collapsible details
          Implemented in `EstimatedTotalSection` so the logic is reusable. */}
      <EstimatedTotalSection bookingTotalModel={bookingTotalModel} />

      {/* Payment method selection / wallet info */}
      <PaymentMethodSection
        ref={paymentRef}
        handleUpdateBookingObject={handleUpdateBookingObject}

        type={type}
        bookingTotalModel={bookingTotalModel}
        distanceInKm={distanceInKm}
        statesToBookingObj={statesToBookingObj}
        userInfo={userInfo}
        coupon={coupon}


        setShowPaymentMessage={setShowPaymentMessage}
        setRequestButtonLoading={setRequestLoading}
        setBookingTotalModel={setBookingTotalModel}
        visionId={visionId}
        refreshBookingTotalModel={refreshBookingTotalModel}

      />


      {/* Pickup & Dropoff timeline and collapsible details */}
      <PickupDropoffSection
        dropOffPersonName={dropOffPersonName}
        dropOffPersonPhone={dropOffPersonPhone}
        pickUpPersonName={pickUpPersonName}
        pickUpPersonPhone={pickUpPersonPhone}
        pickupLocation={pickupLocation}
        stops={stops}
        dropoffLocation={dropoffLocation}
        handleUpdateBookingObject={handleUpdateBookingObject}
        pickUpInstructions={pickUpInstructions}
        dropOffInstructions={dropOffInstructions}
      />

  

      {/* Truck & crew summary (vehicle size, crew available) */}
      <TruckAndCrewSection handleUpdateBookingObject={handleUpdateBookingObject} completeBookingObj={completeBookingObj} handleEstimatePrice={handleEstimatePrice} />

      {/* Arrival window / chosen time slot - accepts `exactTime` prop */}
      <ArrivalWindowSection exactTime={exactTime} deliveryDate={deliveryDate} handleUpdateBookingObject={handleUpdateBookingObject} />

      {/* List of items being moved + collapse behavior */}
      <WhatYoureMovingSection items={items} handleUpdateBookingObject={handleUpdateBookingObject} />

      {/* Free text / additional notes field */}
      <AdditionalNotesSection />

      {/* Primary call-to-action */}
      <div className="mb-[19px] flex flex-col gap-2">
        <button
          onClick={() => paymentRef.current?.handlePlaceOrder?.()}
          disabled={requestLoading}
          className={`w-full h-[48px] rounded-[15px] font-semibold text-[16px] text-white ${requestLoading ? 'bg-slate-500 cursor-not-allowed' : 'bg-gradient-to-b from-[#55AB26] to-[#8EDA65]'}`}>
          {requestLoading ? (
            <div className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              <span>Requesting...</span>
            </div>
          ) : (
            'Book your move'
          )}
        </button>


      </div>



      {/* Small legal / footer note */}
      <div

        className="text-center text-[#7876A1] text-[11px]">
        Powered by Moovez Vision Agent™. By clicking “Book your move”, you agree to the <strong className="cursor-pointer" onClick={() => window.open('https://moovez.ca/terms-of-service', '_blank')}>Terms of Service</strong> and <strong className="cursor-pointer" onClick={() => window.open('https://moovez.ca/privacy-policy', '_blank')}>Privacy Policy</strong>.
      </div>

    </div>
  );
};

export default SummaryScreen;
